<script lang="ts" setup>
import { AlertCircle } from 'lucide-vue-next';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import { useProfile } from '@/profile/useProfile';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useLogger } from '@nzyme/vue-ioc/useLogger.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import { Reveal } from '@nzyme/vue/components/Reveal.jsx';
import { ActorClient } from '@sigma/actors-client/ActorClient.js';
import { ChatActor } from '@sigma/api-core/actors/ChatActor.js';
import type { ChatProfileDataRequest } from '@sigma/chat-common/ChatEvent.js';
import { getIsLastEvent } from '@sigma/chat-common/utils/getIsLastEvent.js';
import { defineChatEventProps } from '@sigma/client/chat/defineChatEventProps.js';
import type { PatientProfile } from '@sigma/common/patient/PatientProfile.js';
import ChatEventWrapper from '@sigma/ui/chat/ChatEventWrapper.vue';
import InfoBox from '@sigma/ui/InfoBox.vue';
import Loader from '@sigma/ui/Loader.vue';
import PatientProfileForm from '@sigma/ui/patients/PatientProfileForm.vue';

import chatCss from './chat.module.scss';
import * as l from './ChatProfileDataRequest.loc.js';

const props = defineProps(defineChatEventProps<ChatProfileDataRequest>());

const t = useTranslate();
const actorClient = useService(ActorClient);
const chatActorRef = actorClient.actor(ChatActor);
const logger = useLogger('ChatProfileDataRequest');
const { getProfile, updateProfile } = useProfile();

const profileData = ref<PatientProfile>();
const loading = ref(true);
const error = ref<string | null>(null);

let mounted = true;
onBeforeUnmount(() => {
  mounted = false;
});

const isLast = computed(() => getIsLastEvent(props.chat.events, props.event));

onMounted(async () => {
  if (!isLast.value) {
    loading.value = false;
    return;
  }

  try {
    const result = await getProfile();
    profileData.value = result.profile;
  } finally {
    if (mounted) {
      loading.value = false;
    }
  }
});

async function saveProfile(profile: PatientProfile) {
  await updateProfile(profile);

  if (!mounted) {
    return;
  }

  try {
    if (props.chat.chatId) {
      await chatActorRef.requests.pingAndWait(props.chat.chatId);
    }
  } catch (err) {
    logger.error('Failed to notify agent after profile save', { error: err });

    if (mounted) {
      error.value = t(l.saveError);
    }
  }
}
</script>

<template>
  <ChatEventWrapper
    :event="event"
    :class="[css.event, chatCss.container]"
  >
    <Reveal>
      <Loader
        v-if="loading"
        :class="css.loader"
      />

      <template v-else-if="isLast">
        <PatientProfileForm
          v-if="profileData"
          :title="t(l.profileTitle)"
          :subtitle="t(l.profileSubtitle)"
          :profile="profileData"
          :submit="saveProfile"
        />

        <InfoBox
          v-if="error"
          color="red"
          :icon="AlertCircle"
        >
          {{ error }}
        </InfoBox>
      </template>
    </Reveal>
  </ChatEventWrapper>
</template>

<style lang="scss" module="css">
.event {
  display: flex;
  flex-direction: column;
  padding-block: $spacing-16;
}

.loader {
  width: 24px;
  height: 24px;
  margin-inline: auto;
  color: $color-blue-600;
}
</style>
